/**
 * Composition root: builds the OIDC session, selection stores, i18n, API client,
 * connectivity monitor and PWA update state once per page load. Components reach them
 * through `ServicesContext`.
 */
import { createContext, useContext } from 'react';
import type { i18n as I18n } from 'i18next';
import type { UserManager } from 'oidc-client-ts';
import type { ApiClient } from '@macksons/contracts';
import { createI18n, type Locale } from '@macksons/i18n';
import {
  createLocaleStore,
  createOutletStore,
  createSessionRestorer,
  createStaffApiClient,
  createStaffUserManager,
  createTenantStore,
  type KeyValueStorage,
  type SelectionStore,
} from '@macksons/web-client';
import { createSession, type Session } from './auth/session';
import { absoluteUrl, type OperatorConfig } from './config';
import { createConnectivity, type Connectivity, type ConnectivityOptions } from './connectivity/connectivity';
import { createClaimTransport } from './offline/claim-transport';
import { createScanQueue, webLock, type ChangeChannel, type ScanQueue } from './offline/scan-queue';
import { createIdbScanStore } from './offline/scan-store';
import { listenForFlushRequests, requestScanSync } from './offline/sync-protocol';
import { createPwaUpdates, type PwaUpdates, type RegisterSW } from './pwa/register-sw';
import { createCameraScanner, type CameraScanner } from './scanner/camera';

export interface OperatorServices {
  config: OperatorConfig;
  session: Session;
  localeStore: SelectionStore<Locale>;
  tenantStore: SelectionStore<string | undefined>;
  outletStore: SelectionStore<string | undefined>;
  i18n: I18n;
  /** Staff API client: Bearer token, `X-Tenant-Id` from tenantStore, `Accept-Language`. */
  client: ApiClient;
  connectivity: Connectivity;
  pwa: PwaUpdates;
  scanQueue: ScanQueue | undefined;
  /** Camera QR decoder (native BarcodeDetector or lazy-loaded zxing). */
  cameraScanner: CameraScanner;
}

export interface ServiceOverrides {
  storage?: KeyValueStorage;
  fetch?: typeof globalThis.fetch;
  userManager?: UserManager;
  registerSW?: RegisterSW;
  connectivity?: Partial<ConnectivityOptions>;
  /**
   * Offline scan queue. Default: IndexedDB-backed when `indexedDB` exists (browsers),
   * none otherwise (tests that do not exercise it). `null` disables it.
   */
  scanQueue?: ScanQueue | null;
  cameraScanner?: CameraScanner;
}

export function createServices(config: OperatorConfig, overrides: ServiceOverrides = {}): OperatorServices {
  const userManager =
    overrides.userManager ??
    createStaffUserManager({ authority: config.oidcAuthority, clientId: config.oidcClientId, appOrigin: config.appOrigin });
  const currentUser = createSessionRestorer(userManager);
  const session = createSession({ userManager, currentUser });
  const localeStore = createLocaleStore(overrides.storage);
  const tenantStore = createTenantStore(overrides.storage);
  const outletStore = createOutletStore(overrides.storage);
  const i18n = createI18n({ locale: localeStore.get() });
  // Runtime language switch (Req 22.2): i18next follows the store, no reload or re-login.
  localeStore.subscribe(() => void i18n.changeLanguage(localeStore.get()));

  const baseFetch = overrides.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(input, init));
  // A 401 from the API means the token is no longer accepted: back to sign-in.
  const apiFetch: typeof globalThis.fetch = async (input, init) => {
    const res = await baseFetch(input, init);
    if (res.status === 401) void session.expire();
    return res;
  };
  const client = createStaffApiClient({
    baseUrl: absoluteUrl(config, config.apiBaseUrl),
    getAccessToken: async () => (await currentUser())?.access_token,
    getTenantId: tenantStore.get,
    getLocale: localeStore.get,
    fetch: apiFetch,
  });

  const connectivity = createConnectivity({
    healthUrl: absoluteUrl(config, config.healthUrl),
    fetch: baseFetch,
    ...overrides.connectivity,
  });
  const swContainer = typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : undefined;
  const scanQueue =
    overrides.scanQueue === undefined
      ? typeof indexedDB === 'undefined'
        ? undefined
        : createScanQueue({
            store: createIdbScanStore(),
            transport: createClaimTransport(client),
            // Frozen on the item at scan time (Req 13.1): later outlet switches do not apply.
            getScope: () => {
              const s = session.get();
              const tenantId = tenantStore.get();
              const outletId = outletStore.get();
              return s.status === 'signedIn' && tenantId && outletId
                ? { tenantId, outletId, operatorUserId: s.claims.sub }
                : undefined;
            },
            getOperatorId: () => {
              const s = session.get();
              return s.status === 'signedIn' ? s.claims.sub : undefined;
            },
            canSubmit: () => connectivity.get().online,
            lock: webLock(),
            ...(typeof BroadcastChannel === 'undefined'
              ? {}
              : { channel: new BroadcastChannel('macksons-scan-queue') as unknown as ChangeChannel }),
            onEnqueued: () => void requestScanSync(swContainer),
          })
      : (overrides.scanQueue ?? undefined);
  if (scanQueue) {
    // Flush triggers (Req 13.2): offline → online, the API answering again after an unknown
    // or failed ping (covers start-up with a non-empty queue), sign-in (after a 401 stop),
    // and the service worker relaying a Background Sync.
    connectivity.onReconnect(() => void scanQueue.flush());
    let reachable = connectivity.get().api === 'reachable';
    connectivity.subscribe(() => {
      const now = connectivity.get().api === 'reachable';
      if (now && !reachable) void scanQueue.flush();
      reachable = now;
    });
    let signedIn = session.get().status === 'signedIn';
    session.subscribe(() => {
      const now = session.get().status === 'signedIn';
      if (now && !signedIn) void scanQueue.flush();
      signedIn = now;
    });
    listenForFlushRequests(swContainer, () => void scanQueue.flush());
  }

  return {
    config,
    session,
    localeStore,
    tenantStore,
    outletStore,
    i18n,
    client,
    connectivity,
    pwa: createPwaUpdates(overrides.registerSW),
    scanQueue,
    cameraScanner: overrides.cameraScanner ?? createCameraScanner(),
  };
}

export const ServicesContext = createContext<OperatorServices | null>(null);

export function useServices(): OperatorServices {
  const services = useContext(ServicesContext);
  if (!services) throw new Error('useServices must be used inside <ServicesContext.Provider>');
  return services;
}
